import {
  CalendarDate,
  type DateValue,
  fromDate,
  getLocalTimeZone,
  now,
  Time,
  today,
} from "@internationalized/date"
import { isValidDate } from "@prismicio/editor-support/Date"
import { refContainsTarget } from "@prismicio/editor-support/React"
import { type AriaDatePickerProps, useDatePicker } from "@react-aria/datepicker"
import { useDatePickerState } from "@react-stately/datepicker"
import { useRef } from "react"

import type { SX } from "../../theme"
import { Calendar } from "../Calendar"
import { Card, CardContent } from "../Card"
import { Popover, type PopoverInteractOutsideEvent } from "../Popover"
import { ToggleButton } from "../ToggleButton"
import styles from "./DateInput.module.css"
import { DateSegments } from "./DateSegments"

/* Internal component; you probably want FormDateInput instead. */

export type DateInputProps = {
  value?: Date
  mode: Mode
  defaultTime?: Time
  disabled?: boolean
  /**
   * The minimum allowed date that a user may select.
   */
  minValue?: "today"
  "aria-labelledby": string
  sx?: SX
  onValueChange: (value?: Date) => void
}

type Mode = "date" | "date-time"

export function DateInput(props: DateInputProps) {
  const {
    disabled = false,
    value,
    onValueChange,
    minValue,
    mode,
    defaultTime,
    sx,
    "aria-labelledby": ariaLabelledBy,
  } = props

  const placeholder = placeholderValue(mode)

  const datePickerProps: AriaDatePickerProps<DateValue> = {
    "aria-labelledby": ariaLabelledBy,
    isDisabled: disabled,
    placeholderValue: placeholder,
    value: toAriaDate(value, mode),
    onChange,
  }

  const state = useDatePickerState(datePickerProps)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const { fieldProps, calendarProps } = useDatePicker(
    datePickerProps,
    state,
    rootRef,
  )

  function onEscapeKeyDown() {
    state.setOpen(false)
    buttonRef.current?.focus()
  }

  function onInteractOutside(event: PopoverInteractOutsideEvent) {
    const target = event.target

    // If the interaction happens inside the button, we let the button handle it.
    if (buttonRef.current === target || refContainsTarget(buttonRef, target))
      return

    state.setOpen(false)
  }

  function onCalendarChange(newValue: DateValue) {
    calendarProps.onChange?.(newValue)
    setTimeout(() => buttonRef.current?.focus())
  }

  // Note: react-aria doesn't properly declare this null option in their types but it can happen.
  function onChange(ariaDate: DateValue | null) {
    if (!ariaDate) return onValueChange(undefined)

    const date = ariaDate.toDate(getLocalTimeZone())
    onValueChange(date)
  }

  return (
    <Popover
      align="start"
      sideOffset={2}
      open={state.isOpen}
      onEscapeKeyDown={onEscapeKeyDown}
      onInteractOutside={onInteractOutside}
      anchor={
        <div className={styles.dateInput} style={sx} ref={rootRef}>
          <div className={styles.dateSegments}>
            <DateSegments
              ariaDateFieldProps={fieldProps}
              defaultTime={defaultTime}
              placeholderValue={placeholder}
            />
          </div>
          <ToggleButton
            hiddenLabel="Toggle date picker"
            disabled={disabled}
            ref={buttonRef}
            pressed={state.isOpen}
            size="small"
            color="grey"
            startIcon="calendarToday"
            onPressedChange={(pressed) => state.setOpen(pressed)}
          />
        </div>
      }
    >
      <Card radius={4} paddingBlock={16}>
        <CardContent>
          <Calendar
            minValue={minValue}
            value={calendarProps.value ?? undefined}
            onValueChange={onCalendarChange}
          />
        </CardContent>
      </Card>
    </Popover>
  )
}

function placeholderValue(mode: Mode) {
  const timeZone = getLocalTimeZone()

  switch (mode) {
    case "date":
      return today(timeZone)
    case "date-time":
      return now(timeZone)
  }
}

function toAriaDate(date: Date | undefined, mode: Mode) {
  if (!date) return null
  if (!isValidDate(date)) return null

  try {
    switch (mode) {
      case "date":
        return new CalendarDate(
          date.getUTCFullYear(),
          date.getUTCMonth() + 1,
          date.getUTCDate(),
        )
      case "date-time":
        return fromDate(date, getLocalTimeZone())
    }
  } catch (_) {
    return null
  }
}
